import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { ApiError, approveReissue, getReissueRequest, listReissueRequests, rejectReissue, type ReissueQueueItem } from "@/api/client";
import { useSession } from "@/auth/session";
import { ApprovalTabs } from "@/components/ApprovalTabs";
import { DataTable, type Column } from "@/components/DataTable";
import { formatDate } from "@/components/format";
import { PagedFooter } from "@/components/Pagination";
import { pageParams } from "@/routes/paging";
import { EmptyState, ErrorBanner, StatusDot, ToolbarButton, WorkspaceHeader } from "@/components/ui";
import { MD, useMediaQuery } from "@/components/useMediaQuery";
import { useRevealPanel } from "@/components/useRevealPanel";
import { reissueIssueText } from "./issues";

const reissuesApi = getRouteApi("/staff/approvals/reissues");

/** A 422 from approve names the failing check in details; show its friendly text. */
function friendly(error: unknown): unknown {
  if (error instanceof ApiError && error.status === 422) {
    const issue = error.details[0]?.issue;
    if (issue) return new Error(reissueIssueText(issue));
  }
  return error;
}

export function ReissueQueue() {
  const { me, can } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const search = reissuesApi.useSearch();
  const { page, page_size } = search;
  const activeID = search.request ?? null;
  const [comment, setComment] = useState("");
  const [approvedCertificate, setApprovedCertificate] = useState<string | null>(null);

  const requests = useQuery({
    queryKey: ["reissue-requests", "pending", page, page_size],
    queryFn: () => listReissueRequests({ status: "pending", ...pageParams(search) }),
    refetchInterval: 15_000,
  });
  const items = requests.data?.items ?? [];
  const onPage = items.find((item) => item.id === activeID) ?? null;
  // A linked request may sit on another page, so look it up before calling it decided.
  const linked = useQuery({
    queryKey: ["reissue-request", activeID],
    queryFn: () => getReissueRequest(activeID!),
    enabled: activeID !== null && requests.isSuccess && onPage === null,
  });
  const active = onPage ?? (linked.data?.status === "pending" ? linked.data : null);
  // A linked request that has left the queue (decided by someone else).
  const stale = activeID !== null && active === null && requests.isSuccess && !linked.isPending;
  const mine = active !== null && active.requested_by.id === me?.user.id;

  const select = (request: string | undefined) => {
    // Clears carry-over state so a comment, error or result from the previous request never applies to this one.
    setComment("");
    setApprovedCertificate(null);
    approve.reset();
    reject.reset();
    void navigate({ to: "/approvals/reissues", search: { request, page, page_size } });
  };

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["reissue-requests"] });
    await queryClient.invalidateQueries({ queryKey: ["certificate"] });
    await queryClient.invalidateQueries({ queryKey: ["certificates"] });
  };
  // A 409 means someone else decided it; refetch so the view moves to "No longer pending".
  const onConflict = (error: unknown) => {
    if (error instanceof ApiError && error.status === 409) {
      void queryClient.invalidateQueries({ queryKey: ["reissue-requests", "pending"] });
    }
  };
  const approve = useMutation({
    mutationFn: approveReissue,
    onSuccess: async (result) => {
      setApprovedCertificate(result.certificate_id);
      await refresh();
      await queryClient.invalidateQueries({ queryKey: ["reissue-payments"] });
      await queryClient.invalidateQueries({ queryKey: ["payments"] });
      await queryClient.invalidateQueries({ queryKey: ["revocations"] });
    },
    onError: onConflict,
  });
  const reject = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => rejectReissue(id, note),
    onSuccess: async () => {
      select(undefined);
      await refresh();
    },
    onError: onConflict,
  });
  const busy = approve.isPending || reject.isPending;
  const { panelRef, headingRef, reveal } = useRevealPanel(activeID);
  const wide = useMediaQuery(MD);
  const columns: Column<ReissueQueueItem>[] = [
    {
      key: "certificate",
      header: "Certificate",
      // A card is one selection button, so only the table row may carry the link.
      cell: (item) =>
        wide && can("certificate.read") ? (
          <Link
            to="/certificates/$certificateId"
            params={{ certificateId: item.certificate_id }}
            onClick={(e) => e.stopPropagation()}
            className="font-mono text-xs font-medium underline"
          >
            {item.doc_control_id}
          </Link>
        ) : (
          <span className="font-mono text-xs font-medium">{item.doc_control_id}</span>
        ),
    },
    {
      key: "member",
      header: "Member",
      primary: true,
      cell: (item) => <span className="font-medium text-zinc-950">{item.member_full_name}</span>,
    },
    {
      key: "reason",
      header: "Reason",
      className: "max-w-48 truncate",
      cell: (item) => <span className="text-zinc-700">{item.reason}</span>,
    },
    {
      key: "mode",
      header: "Mode",
      cell: (item) => <span className="text-zinc-700">{item.mode === "revoke_original" ? "Revoke" : "Keep"}</span>,
    },
    {
      key: "fee",
      header: "Fee",
      cell: (item) => <span className="text-zinc-700">{item.payment_id ? "Payment" : "Waived"}</span>,
    },
    {
      key: "requested",
      header: "Requested",
      cell: (item) => (
        <span className="text-zinc-500">
          {item.requested_by.name} · {formatDate(item.requested_at)}
        </span>
      ),
    },
  ];

  const pick = (id: string) => {
    reveal(id);
    select(id);
  };

  return (
    <section className="space-y-5">
      <ApprovalTabs />
      <WorkspaceHeader
        title="Re-issue requests"
        detail={`${requests.data?.total ?? 0} request${requests.data?.total === 1 ? "" : "s"} awaiting a second approver`}
        action={<StatusDot label={requests.isFetching ? "Refreshing" : "Live"} />}
      />
      <ErrorBanner error={requests.error ?? friendly(approve.error) ?? reject.error} />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(360px,0.8fr)] xl:gap-5">
        <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
          {items.length === 0 ? (
            <EmptyState title="No pending re-issues" detail="Requests appear here until a second person decides them." />
          ) : (
            <div className="p-3 md:p-0">
              <DataTable
                rows={items}
                columns={columns}
                rowKey={(item) => item.id}
                label="Pending re-issue requests"
                onSelect={(item) => pick(item.id)}
                selectedKey={activeID ?? undefined}
              />
            </div>
          )}
          <PagedFooter data={requests.data} page={page} pageSize={page_size} />
        </div>

        <aside ref={panelRef} className="h-fit rounded-lg border border-zinc-200 bg-white p-4">
          <h2 ref={headingRef} tabIndex={-1} className="text-sm font-semibold focus:outline-none">
            Decision detail
          </h2>
          {approvedCertificate ? (
            <p className="mt-4 text-sm">
              {can("certificate.read") ? (
                <Link
                  to="/certificates/$certificateId"
                  params={{ certificateId: approvedCertificate }}
                  className="font-medium text-zinc-950 underline"
                >
                  Approved. Open the new certificate.
                </Link>
              ) : (
                "Approved. The new certificate has been issued."
              )}
            </p>
          ) : active ? (
            <div className="mt-4 space-y-4 text-sm">
              <div>
                {can("certificate.read") ? (
                  <Link
                    to="/certificates/$certificateId"
                    params={{ certificateId: active.certificate_id }}
                    className="font-mono text-xs font-medium underline"
                  >
                    {active.doc_control_id}
                  </Link>
                ) : (
                  <span className="font-mono text-xs font-medium">{active.doc_control_id}</span>
                )}
                <p className="mt-1 text-zinc-800">{active.member_full_name}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-500">Reason given by {active.requested_by.name}</p>
                <p className="mt-1 whitespace-pre-wrap text-zinc-800">{active.reason}</p>
              </div>
              <div className="space-y-1 text-xs text-zinc-600">
                <p>
                  {active.mode === "revoke_original"
                    ? "Revokes this certificate (a second revocation approval follows)"
                    : "Keeps this certificate valid"}
                </p>
                <p>
                  {active.payment_id ? `Payment ${active.payment_id.slice(0, 8)}` : `Fee waived: ${active.fee_waiver_reason ?? "-"}`}
                </p>
                <p>Affidavit {active.affidavit_reference}</p>
              </div>
              {mine ? (
                <p className="rounded-md bg-amber-50 px-3 py-2 text-amber-800">
                  You requested this re-issue. Another approver must decide it.
                </p>
              ) : (
                <>
                  <label className="block">
                    <span className="font-medium text-zinc-700">Rejection comment</span>
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      rows={3}
                      maxLength={2000}
                      className="mt-1 w-full resize-none rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
                    />
                  </label>
                  <div className="flex gap-2">
                    <ToolbarButton tone="primary" disabled={busy} onClick={() => approve.mutate(active.id)}>
                      Approve re-issue
                    </ToolbarButton>
                    <ToolbarButton
                      disabled={busy || comment.trim() === ""}
                      onClick={() => reject.mutate({ id: active.id, note: comment.trim() })}
                    >
                      Reject
                    </ToolbarButton>
                  </div>
                </>
              )}
            </div>
          ) : stale ? (
            <div className="mt-4">
              <EmptyState title="No longer pending" detail="Someone else has already decided this request." compact />
            </div>
          ) : (
            <div className="mt-4">
              <EmptyState title="Select a request" detail="The request details and decision controls appear here." compact />
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}
